import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { API_BASE } from '../config';
import { useTranslation } from '../translations';
import { useToast } from '../components/ui/Toast';
import { useConfirm } from '../components/ui/Modal';
import { Banner } from '../components/ui/Banner';
import { EmptyState } from '../components/ui/EmptyState';
import PaginationCompact, { DEFAULT_PAGE_SIZE } from '../components/PaginationCompact';
import { formatMoney, formatDateTime } from '../utils/format';
import { formatYMD, parseYMD } from '../utils/contract';
import { pluralCategory } from '../utils/reservationPicker';
import { CheckIcon, AlertIcon, SheetIcon, CartIcon } from '../ui/icons';
import { METODY_NA_MIEJSCU, nazwaMetody } from '../utils/metodaWplaty';
import {
  PanelContainer, Title, Subtitle, PageHeader,
  StatGrid, StatCard, StatIcon, StatValue, StatLabel,
  Badge, TableWrap as BaseTableWrap,
  FilterField as Field, FilterPillGroup as PillGroup, FilterPill as Pill, LinkButton as SourceLink,
} from '../ui/common';

// P3.3: lista płatności dla właściciela (kto, ile, kiedy, status, źródło).
// Do uzgadniania wpłat z wyciągiem; dane z GET /api/payments (org-scoped, admin).
// Design: jeden język statusów (filtr-pigułki = badge w tabeli), kwoty w cyfrach
// tabelarycznych do prawej, główna metryka z wyciszonymi groszami.

// Rodzaj prośby o płatność -> ton koloru. Kaucja dostaje `warning`, bo jako jedyna
// BLOKUJE pieniądze zamiast je pobierać, więc pomyłka przy niej kosztuje najwięcej.
const TYPE_TONE = { deposit: 'warning' };
const TYPE_KEYS = { deposit: 'deposit', advance: 'advance', balance: 'balance' };

// Status -> ton odznaki (jeden z czterech: success/warning/danger/info). Kaucja ściągnięta
// (`captured`) dostaje `success` (pieniądze bezpiecznie zabezpieczone), a stany bez realnego
// ryzyka (zwrócone/anulowane/zwolnione) dostają `info` jako neutralny, spokojny kolor.
const STATUS_TONE = {
  paid: 'success',
  pending: 'warning',
  failed: 'danger',
  refunded: 'info',
  cancelled: 'info',
  partially_refunded: 'info',
  authorized: 'warning',
  captured: 'success',
  released: 'info',
};
const STATUS_KEYS = {
  paid: 'paid',
  pending: 'pending',
  failed: 'failed',
  refunded: 'refunded',
  cancelled: 'cancelled',
  partially_refunded: 'partiallyRefunded',
  authorized: 'authorized',
  captured: 'captured',
  released: 'released',
};
const FILTERABLE = ['paid', 'pending', 'failed', 'refunded'];
// Sposób zapłaty (F.10): wpłaty na miejscu po metodzie (także karta z terminala wypożyczalni)
// i wszystko inne jako „online”. Filtr liczy serwer (GET /api/payments?method=…).
const METODY_FILTRA = [...METODY_NA_MIEJSCU, 'online'];

/* --- Metryki: StatGrid/StatCard wspólne z resztą aplikacji (ui/common.js) --- */

/* --- Filtry: pigułki statusów w kolorach tonu + daty z etykietami --- */
const Filters = styled.div`
  display: flex;
  gap: var(--sp-4);
  flex-wrap: wrap;
  align-items: flex-end;
  margin-bottom: var(--sp-4);
`;
/* Pigułki (Pill/PillGroup) i etykieta filtra (Field) pochodzą z ui/common.js — te same
   co na liście faktur, więc filtr statusu wygląda identycznie na obu stronach. */
const DateInput = styled.input`
  padding: var(--sp-2) var(--sp-3);
  border: 1px solid var(--input-border);
  border-radius: var(--r-md);
  background: var(--input-bg);
  color: var(--text);
  font-size: var(--fs-sm);
  font-variant-numeric: tabular-nums;
  &:focus { outline: none; border-color: var(--primary); box-shadow: var(--focus-ring); }
`;
const Search = styled.input`
  padding: var(--sp-2) var(--sp-3);
  border: 1px solid var(--input-border);
  border-radius: var(--r-md);
  background: var(--input-bg);
  color: var(--text);
  font-size: var(--fs-base);
  min-width: 200px;
  &:focus { outline: none; border-color: var(--primary); box-shadow: var(--focus-ring); }
`;
const ClearBtn = styled.button`
  background: none;
  border: none;
  padding: var(--sp-2) var(--sp-1);
  color: var(--primary);
  font-size: var(--fs-sm);
  font-weight: 600;
  cursor: pointer;
  &:hover { text-decoration: underline; }
`;

/* --- Tabela: sticky nagłówek, kwoty do prawej, tabular-nums --- */
const TableWrap = styled(BaseTableWrap)`
  max-height: 62vh;
  overflow-y: auto;
`;
const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: var(--fs-base);
  th, td { padding: var(--sp-3) var(--sp-4); text-align: left; }
  th {
    position: sticky;
    top: 0;
    background: var(--card-bg);
    color: var(--text-muted);
    font-size: var(--fs-xs);
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    border-bottom: 1px solid var(--input-border);
    z-index: var(--z-sticky);
  }
  tbody tr { border-bottom: 1px solid var(--input-border); }
  tbody tr:last-child { border-bottom: none; }
  tbody tr:hover { background: var(--hover-bg); }
  td { color: var(--text); }
  th.num, td.num {
    text-align: right;
    font-variant-numeric: tabular-nums;
  }
  td.date { font-variant-numeric: tabular-nums; white-space: nowrap; color: var(--text-muted); }
`;
const ClientCell = styled.div`
  font-weight: 600;
  span { display: block; font-weight: 400; font-size: var(--fs-xs); color: var(--text-muted); }
`;
const TypeLabel = styled.div`
  font-weight: 600;
  font-size: var(--fs-xs);
  margin-top: 2px;
  color: ${p => (p.$tone ? `var(--${p.$tone})` : 'var(--text-muted)')};
`;
const MethodLabel = styled.div`
  font-weight: 500;
  font-size: var(--fs-xs);
  color: var(--text-muted);
`;
const RefundedNote = styled.div`
  font-weight: 400;
  font-size: var(--fs-xs);
  color: var(--text-muted);
`;
const OverpaymentNote = styled.div`
  margin-bottom: var(--sp-1);
  font-size: var(--fs-xs);
  font-weight: 600;
  color: var(--warning);
`;
const NoSource = styled.span`
  color: var(--text-muted);
`;
const PayLinkBlock = styled.div`
  margin-top: var(--sp-2);
`;
const PayLinkLabel = styled.div`
  font-size: var(--fs-xs);
  color: var(--text-muted);
  margin-bottom: var(--sp-1);
`;
const PayLinkInput = styled.input`
  width: 100%;
  min-width: 0;
  font-size: var(--fs-xs);
  padding: var(--sp-1) var(--sp-2);
  border: 1px solid var(--input-border);
  border-radius: var(--r-sm);
  cursor: pointer;
  background: var(--input-bg);
  color: var(--text-muted);
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
`;
const PayLinkHint = styled.div`
  font-size: var(--fs-xs);
  color: var(--text-muted);
  margin-top: 2px;
`;
const Empty = styled.div`
  padding: var(--sp-8) var(--sp-6);
  text-align: center;
  color: var(--text-muted);
  line-height: 1.7;
`;
const BannerSlot = styled.div`
  margin-bottom: var(--sp-4);
`;

// Kwota z groszy jako zwykły string z kropką dziesiętną (bez tysięcznego separatora), do
// wpisania jako wartość domyślna w window.prompt() — MUSI zostać parsowalna z powrotem, więc
// żadnego formatowania Intl (ono dokłada spacje/przecinki zależne od locale).
function groszeToPlainAmount(grosze) {
  const cents = Math.max(0, Math.round(Number(grosze) || 0));
  const whole = Math.floor(cents / 100);
  const rest = String(cents % 100).padStart(2, '0');
  return `${whole}.${rest}`;
}

// Wpis cofnięty przyciskiem „Cofnij wpis” (12.10.2026, test odbiorczy płatności). Ma status
// 'cancelled' jak prośba anulowana przez obsługę albo sprzątaczkę, więc w rejestrze wyglądał tak samo,
// a do tego dalej pokazywał „Datę wpłaty”, choć pieniędzy nie było. Wpisy na miejscu (gotówka,
// przelew) powstają od razu jako opłacone, więc 'cancelled' przy nich znaczy wyłącznie cofnięcie;
// `reversed_at` (od tej zmiany w GET /api/payments) dokłada godzinę.
export function isReversedEntry(r) {
  return !!r && r.status === 'cancelled' && (r.provider === 'offline' || !!r.reversed_at);
}

// Wpis na miejscu, który da się cofnąć („Cofnij wpis”): opłacona zaliczka albo dopłata, a także
// kaucja przyjęta gotówką albo przelewem (status 'captured'), dopóki nic z niej nie oddano. Ta sama
// reguła co POST /api/payments/:id/reverse; oddanie części kaucji to zwrot, nie pomyłka we wpisie.
export function daSieCofnac(r) {
  if (!r || r.provider !== 'offline') return false;
  if (r.type === 'deposit') return r.status === 'captured' && !(Number(r.amount_refunded_grosze) > 0);
  return r.status === 'paid';
}

// Dzień rezerwacji do linku „Rezerwacja #…”. `reservation_start` przychodzi jako znacznik czasu
// w UTC („2026-11-09T23:00:00.000Z” dla 10 listopada w Polsce), a `slice(0, 10)` dawało dzień
// wcześniej, więc kalendarz zaznaczał 9.11 i plan dnia pod oknem był z niewłaściwego dnia.
export function reservationDayParam(value) {
  const d = parseYMD(value);
  return d && !Number.isNaN(d.getTime()) ? formatYMD(d) : '';
}

function PaymentsList() {
  const navigate = useNavigate();
  const { t, currentLanguage } = useTranslation();
  const toast = useToast();
  const confirm = useConfirm();
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ paid_count: 0, paid_sum_grosze: 0, pending_count: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // 403: rejestr jest dla administratora. Wtedy bez kafelków z zerami, filtrów i „Spróbuj ponownie”.
  const [forbidden, setForbidden] = useState(false);
  const [status, setStatus] = useState('');
  const [metoda, setMetoda] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);

  const hasFilters = !!(status || metoda || from || to || q.trim());

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    setForbidden(false);
    try {
      const params = new URLSearchParams();
      if (status) params.set('status', status);
      if (metoda) params.set('method', metoda);
      if (from) params.set('from', from);
      if (to) params.set('to', to);
      if (q.trim()) params.set('q', q.trim());
      const res = await fetch(`${API_BASE}/api/payments?${params}`, { credentials: 'include' });
      const data = await res.json().catch(() => ({}));
      if (res.status === 403) { setForbidden(true); return; }
      if (!res.ok) throw new Error(data.error || t('common.loadError'));
      setRows(data.payments || []);
      setSummary(data.summary || { paid_count: 0, paid_sum_grosze: 0, pending_count: 0 });
    } catch (e) {
      setError(e.message || t('common.loadError'));
    } finally {
      setLoading(false);
    }
  }, [status, metoda, from, to, q, t]);

  useEffect(() => {
    const timer = setTimeout(load, q ? 350 : 0); // debounce dla szukania
    return () => clearTimeout(timer);
  }, [load, q]);

  useEffect(() => { setPage(1); }, [status, metoda, from, to, q]);

  const clearFilters = () => { setStatus(''); setMetoda(''); setFrom(''); setTo(''); setQ(''); };

  const openSource = (r) => {
    if (r.reservation_id && r.reservation_start) {
      const d = reservationDayParam(r.reservation_start);
      navigate(`/calendar?date=${encodeURIComponent(d)}&reservationId=${encodeURIComponent(r.reservation_id)}`);
    }
  };

  // P3.6: zwrot z panelu (pełny lub częściowy). Kwotę wpisuje się w zł; domyślnie cała
  // pozostała. Stripe wykonuje zwrot na koncie wypożyczalni, lista odświeża się od razu.
  const refund = async (r) => {
    const remainingGrosze = Number(r.amount_grosze) - Number(r.amount_refunded_grosze || 0);
    const suggested = groszeToPlainAmount(remainingGrosze);
    const input = window.prompt(t('payments.refundPrompt', { max: suggested }), suggested);
    if (input === null) return;
    const amount = Math.round(Number(String(input).replace(',', '.')) * 100);
    if (!Number.isInteger(amount) || amount < 1 || amount > remainingGrosze) {
      toast.error(t('payments.refundInvalidAmount', { max: suggested }));
      return;
    }
    const ok = await confirm({
      title: t('payments.confirmRefundTitle'),
      message: t('payments.confirmRefundMessage', {
        amount: formatMoney(amount / 100, { locale: currentLanguage }),
        client: r.client_name || '',
      }),
      confirmLabel: t('payments.refundAction'),
      tone: 'danger',
    });
    if (!ok) return;
    try {
      const csrf = localStorage.getItem('csrfToken') || '';
      const res = await fetch(`${API_BASE}/api/payments/${r.id}/refund`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'x-csrf-token': csrf },
        body: JSON.stringify({ amount_grosze: amount }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || t('payments.refundError'));
      load();
    } catch (e) {
      toast.error(e.message || t('payments.refundError'));
    }
  };

  // P3.15: cofnięcie wpisu OFFLINE (gotówka/przelew) — np. pomyłkowo zapisana wpłata.
  // Płatności online zwraca się przyciskiem „Zwróć"; tu tylko wpisy offline.
  const reverse = async (r) => {
    const ok = await confirm({
      title: t('payments.confirmReverseTitle'),
      message: t('payments.confirmReverseMessage', {
        amount: formatMoney(Number(r.amount_grosze) / 100, { locale: currentLanguage }),
        method: nazwaMetody(r.method, t) || t('payments.methodCash'),
      }),
      confirmLabel: t('payments.reverseAction'),
      tone: 'danger',
    });
    if (!ok) return;
    try {
      const csrf = localStorage.getItem('csrfToken') || '';
      const res = await fetch(`${API_BASE}/api/payments/${r.id}/reverse`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'x-csrf-token': csrf },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || t('payments.reverseError'));
      load();
    } catch (e) {
      toast.error(e.message || t('common.error'));
    }
  };

  // P3.22: zamknięcie prośby, której klient nigdy nie otworzy (rozmyślił się, zapłacił gotówką
  // przy ladzie, pomyłkowa kwota). Bez tego wisiałaby jako „Oczekująca" bez końca: „Cofnij wpis"
  // dotyczy tylko gotówki i przelewu, a „Zwróć" wymaga pieniędzy do oddania.
  const cancelRequest = async (r) => {
    const ok = await confirm({
      title: t('payments.confirmCancelTitle'),
      message: t('payments.confirmCancelMessage', {
        amount: formatMoney(Number(r.amount_grosze) / 100, { locale: currentLanguage }),
      }),
      confirmLabel: t('common.confirm'),
      tone: 'danger',
    });
    if (!ok) return;
    try {
      const csrf = localStorage.getItem('csrfToken') || '';
      const res = await fetch(`${API_BASE}/api/payments/${r.id}/cancel-request`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'x-csrf-token': csrf },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || t('payments.cancelRequestError'));
      load();
    } catch (e) {
      toast.error(e.message || t('common.error'));
    }
  };

  const period = from || to ? t('payments.periodSelected') : t('payments.periodTotal');
  // API oddaje liczniki jako tekst („1”), więc `=== 1` nie trafiało nigdy i wychodziło „1 wpłat”;
  // do tego 2-4 to w polskim „wpłaty” (12.10.2026).
  const paidCount = Number(summary.paid_count) || 0;
  const paidCountForm = pluralCategory(paidCount, currentLanguage);
  const paidCountLabel = paidCountForm === 'one' && paidCount === 1
    ? t('payments.paidCountOne')
    : paidCountForm === 'few' ? t('payments.paidCountFew', { count: paidCount }) : t('payments.paidCountMany', { count: paidCount });

  if (forbidden) {
    return (
      <PanelContainer>
        <PageHeader>
          <div>
            <Title>{t('payments.title')}</Title>
            <Subtitle>{t('payments.subtitle')}</Subtitle>
          </div>
        </PageHeader>
        <BannerSlot data-cy="payments-admin-only">
          <Banner tone="info">{t('payments.adminOnly')}</Banner>
        </BannerSlot>
      </PanelContainer>
    );
  }
  const pageCount = Math.max(1, Math.ceil(rows.length / DEFAULT_PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const pageRows = rows.slice((safePage - 1) * DEFAULT_PAGE_SIZE, safePage * DEFAULT_PAGE_SIZE);

  return (
    <PanelContainer>
      <PageHeader>
        <div>
          <Title>{t('payments.title')}</Title>
          <Subtitle>{t('payments.subtitle')}</Subtitle>
        </div>
      </PageHeader>

      {/* Przy błędzie odczytu bez kafelków: domyślne zera wyglądały jak prawdziwe dane. */}
      {!error && (
      <StatGrid data-tour="payments-summary">
        <StatCard>
          <StatIcon $tone="success"><CheckIcon size={20} /></StatIcon>
          <div>
            <StatValue>{formatMoney(Number(summary.paid_sum_grosze || 0) / 100, { locale: currentLanguage })}</StatValue>
            <StatLabel>
              {t('payments.statPaidLabel', { period })}
              {' · '}
              <span data-cy="payments-paid-count">{paidCountLabel}</span>
            </StatLabel>
          </div>
        </StatCard>
        <StatCard>
          <StatIcon $tone="warning"><AlertIcon size={20} /></StatIcon>
          <div>
            <StatValue>{summary.pending_count}</StatValue>
            <StatLabel>{t('payments.statPendingLabel')} · {t('payments.pendingHint')}</StatLabel>
          </div>
        </StatCard>
        <StatCard>
          <StatIcon $tone="info"><SheetIcon size={20} /></StatIcon>
          <div>
            <StatValue>{rows.length}</StatValue>
            <StatLabel>{t('payments.statRowsLabel')} · {hasFilters ? t('payments.afterFilters') : t('payments.recentPayments')}</StatLabel>
          </div>
        </StatCard>
      </StatGrid>
      )}

      <Filters data-tour="payments-filters">
        <Field as="div">
          {t('common.status')}
          <PillGroup role="group" aria-label={t('common.status')}>
            <Pill type="button" $active={status === ''} onClick={() => setStatus('')}>
              {t('common.all')}
            </Pill>
            {FILTERABLE.map((key) => (
              <Pill
                key={key}
                type="button"
                $active={status === key}
                $tone={STATUS_TONE[key]}
                onClick={() => setStatus(status === key ? '' : key)}
              >
                {t(`payments.statusFilter.${STATUS_KEYS[key]}`)}
              </Pill>
            ))}
          </PillGroup>
        </Field>
        <Field as="div">
          {t('payments.filterMethod')}
          <PillGroup role="group" aria-label={t('payments.filterMethod')} data-cy="payments-method-filter">
            <Pill type="button" $active={metoda === ''} onClick={() => setMetoda('')}>
              {t('common.all')}
            </Pill>
            {METODY_FILTRA.map((key) => (
              <Pill
                key={key}
                type="button"
                $active={metoda === key}
                onClick={() => setMetoda(metoda === key ? '' : key)}
                data-cy={`payments-method-${key}`}
              >
                {key === 'online' ? t('payments.methodOnline') : nazwaMetody(key, t)}
              </Pill>
            ))}
          </PillGroup>
        </Field>
        <Field>
          {t('payments.filterDateFrom')}
          <DateInput type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </Field>
        <Field>
          {t('payments.filterDateTo')}
          <DateInput type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </Field>
        <Field>
          {t('payments.filterClientLabel')}
          <Search placeholder={t('payments.filterClientPlaceholder')} value={q} onChange={(e) => setQ(e.target.value)} />
        </Field>
        {hasFilters && <ClearBtn type="button" onClick={clearFilters}>{t('common.clearFilters')}</ClearBtn>}
      </Filters>

      {error && !loading && (
        <BannerSlot>
          <Banner tone="danger" action={{ label: t('common.retry'), onClick: load }}>
            {error}
          </Banner>
        </BannerSlot>
      )}

      <TableWrap>
        {loading ? (
          <Empty>{t('common.loading')}</Empty>
        ) : error ? null : rows.length === 0 ? (
          <EmptyState
            icon={<CartIcon size={40} />}
            title={hasFilters ? t('payments.emptyFilteredTitle') : t('payments.emptyNoneTitle')}
            description={hasFilters ? t('payments.emptyFiltered') : t('payments.emptyNone')}
            action={hasFilters ? { label: t('common.clearFilters'), onClick: clearFilters } : undefined}
          />
        ) : (
          <Table>
            <thead>
              <tr>
                <th>{t('common.date')}</th>
                <th>{t('payments.tableClient')}</th>
                <th className="num">{t('common.amount')}</th>
                <th>{t('common.status')}</th>
                <th>{t('payments.tableSource')}</th>
                <th>{t('payments.tablePaidAt')}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {/* Nadpłata jest własnością REZERWACJI, a nie pojedynczej wpłaty, więc przy
                  kilku oznaczonych płatnościach tej samej rezerwacji ta sama kwota pokazywała
                  się kilka razy i wyglądała jak wielokrotna nadwyżka. Pokazujemy przy PIERWSZEJ
                  napotkanej NA STRONIE. Znalezione w code review PR #107. */}
              {(() => { const pokazanaNadplata = new Set(); return pageRows.map((r) => {
                const cofniety = isReversedEntry(r);
                const tone = STATUS_TONE[r.status] || 'info';
                const statusLabel = cofniety
                  ? t('payments.status.reversed')
                  : (STATUS_KEYS[r.status] ? t(`payments.status.${STATUS_KEYS[r.status]}`) : r.status);
                const refundable = ['paid', 'partially_refunded'].includes(r.status)
                  && r.type !== 'deposit'
                  && r.provider === 'stripe' && r.gateway_order_id
                  && Number(r.amount_refunded_grosze || 0) < Number(r.amount_grosze);
                return (
                  <tr key={r.id}>
                    <td className="date">{formatDateTime(r.created_at, { locale: currentLanguage })}</td>
                    <td>
                      <ClientCell>
                        {r.client_name || t('payments.noClientData')}
                        {r.client_email ? <span>{r.client_email}</span> : null}
                      </ClientCell>
                    </td>
                    <td className="num" style={{ fontWeight: 700 }}>
                      {formatMoney(Number(r.amount_grosze) / 100, { locale: currentLanguage })}
                      {/* RODZAJ prośby. Lista pokazywała samą kwotę, więc kaucja i płatność na tę
                          samą sumę wyglądały identycznie i nie dało się stwierdzić, którą pozycję
                          się wycofuje. Zapytał o to Piotrek 2026-08-04, mając obok siebie prośbę
                          o kaucję i o dopłatę, obie na 75 zł. */}
                      <TypeLabel $tone={TYPE_TONE[r.type]}>
                        {TYPE_KEYS[r.type] ? t(`payments.type.${TYPE_KEYS[r.type]}`) : r.type}
                      </TypeLabel>
                      {r.provider === 'offline' && nazwaMetody(r.method, t) && (
                        <MethodLabel data-cy="payments-method">{nazwaMetody(r.method, t)}</MethodLabel>
                      )}
                      {Number(r.amount_refunded_grosze || 0) > 0 && (
                        <RefundedNote>
                          {t('payments.refundedAmount', { amount: formatMoney(Number(r.amount_refunded_grosze) / 100, { locale: currentLanguage }) })}
                        </RefundedNote>
                      )}
                    </td>
                    <td>
                      <Badge $tone={tone} data-cy="payments-status">{statusLabel}</Badge>
                      {cofniety && (
                        <RefundedNote data-cy="payments-reversed-note">
                          {r.reversed_at
                            ? t('payments.reversedAt', { date: formatDateTime(r.reversed_at, { locale: currentLanguage }) })
                            : t('payments.reversedNote')}
                        </RefundedNote>
                      )}
                    </td>
                    <td>
                      {r.reservation_id ? (
                        <>
                          <SourceLink type="button" onClick={() => openSource(r)}>{t('payments.reservationRef', { id: r.reservation_id })}</SourceLink>
                          {/* Stan rezerwacji przy wpłacie (12.10.2026, pole z GET /api/payments):
                              wpłata przy anulowanej rezerwacji wyglądała jak każda inna. */}
                          {r.reservation_cancelled && (
                            <RefundedNote data-cy="payments-reservation-cancelled">{t('payments.reservationCancelled')}</RefundedNote>
                          )}
                        </>
                      ) : r.offer_id ? (
                        <span>{t('payments.offerRef', { seq: r.offer_seq || r.offer_id })}</span>
                      ) : (
                        <NoSource>{t('payments.noSource')}</NoSource>
                      )}
                    </td>
                    {/* Cofnięty wpis to pomyłka (pieniędzy nie było), więc bez „Daty wpłaty”. */}
                    <td className="date">{r.paid_at && !cofniety ? formatDateTime(r.paid_at, { locale: currentLanguage }) : ''}</td>
                    <td>
                      {/* Nadpłata: rezerwacja zebrała więcej, niż jest warta. Wykrywane przy
                          rozliczeniu, ale log w konsoli serwera jest dla wypożyczalni niewidoczny,
                          a to ona decyduje o zwrocie. Dlatego stoi DOKŁADNIE przy przycisku
                          „Zwróć", czyli tam, gdzie się na to reaguje. */}
                      {/* „Pierwszy wygrywa" potrafilo umiescic plakietke na wierszu juz w pelni
                          zwroconym, podczas gdy przycisk „Zwróć" stal przy innym. Plakietka ma
                          byc TAM, gdzie sie na nia reaguje. Znalezione w code review PR #108. */}
                      {Number(r.overpayment_grosze) > 0 && refundable && !pokazanaNadplata.has(r.reservation_id) && (pokazanaNadplata.add(r.reservation_id), true) && (
                        <OverpaymentNote>
                          {t('payments.overpayment', { amount: formatMoney(Number(r.overpayment_grosze) / 100, { locale: currentLanguage }) })}
                        </OverpaymentNote>
                      )}
                      {/* Wpłata, która przyszła PO anulowaniu rezerwacji (klient zapłacił na otwartej
                          stronie płatności): w całości do zwrotu, bez automatycznego zwrotu. */}
                      {r.paid_after_cancel && ['paid', 'partially_refunded'].includes(r.status)
                        && Number(r.amount_refunded_grosze || 0) < Number(r.amount_grosze) && (
                        <OverpaymentNote data-cy="payments-paid-after-cancel">{t('payments.paidAfterCancel')}</OverpaymentNote>
                      )}
                      {/* Blokada kaucji założona na karcie, gdy rezerwacja była już anulowana albo miała
                          inną aktywną kaucję (przegląd poprawek PR #214, B-2). Zwalnia się ją w oknie
                          rezerwacji, więc plakietka mówi, co zrobić; znika po zwolnieniu (status). */}
                      {r.status === 'authorized' && r.deposit_to_release === 'reservation_cancelled' && (
                        <OverpaymentNote data-cy="payments-deposit-to-release">{t('payments.depositAfterCancel')}</OverpaymentNote>
                      )}
                      {r.status === 'authorized' && r.deposit_to_release === 'second_deposit' && (
                        <OverpaymentNote data-cy="payments-deposit-to-release">{t('payments.secondDeposit')}</OverpaymentNote>
                      )}
                      {refundable && (
                        <SourceLink type="button" $tone="danger" onClick={() => refund(r)}>
                          {t('payments.refundAction')}
                        </SourceLink>
                      )}
                      {/* Kaucja przyjęta na miejscu ma status 'captured', nie 'paid' (przegląd PR #212,
                          E-4). Pomyłkowy wpis kaucji (zła metoda, kaucji jeszcze nie wpłacono) dało
                          się dotąd tylko „oddać”, czyli zapisać fałszywą historię „przyjęto i
                          oddano”. Serwer cofa ją, dopóki nic z niej nie oddano; rejestr i trasa
                          cofnięcia są wyłącznie dla administratora. */}
                      {daSieCofnac(r) && (
                        <SourceLink type="button" $tone="danger" data-cy="payments-reverse" onClick={() => reverse(r)}>
                          {t('payments.reverseAction')}
                        </SourceLink>
                      )}
                      {/* P3.22: tylko prośby czekające na klienta. Opłaconą zwraca się, a nie anuluje. */}
                      {/* Także po automatycznym anulowaniu (bez `cancelled_by`): taki wiersz ma
                          nadal PŁATNY link, bo bramka w pay/:token reaguje wyłącznie na ręczne
                          wycofanie. Bez tego przycisku obsługa traciła jedyny sposób jego
                          zablokowania. Odsłonięte w security review PR #108.

                          `provider !== 'offline'`: cofnięty wpis gotówkowy ma ten sam status
                          i też nie ma `cancelled_by`, ale nigdy nie miał linku. Oferowanie przy
                          nim „Zablokuj link" kończyło się ubiciem żywego linku do zaliczki. */}
                      {(r.status === 'pending' || ((r.status === 'cancelled' && !r.cancelled_manually) || r.status === 'failed') && r.provider !== 'offline') && (
                        <SourceLink type="button" $tone="danger" onClick={() => cancelRequest(r)}>
                          {r.status === 'pending' ? t('payments.cancelRequestAction') : t('payments.blockLinkAction')}
                        </SourceLink>
                      )}
                      {/* Adres WPROST, do zaznaczenia myszką. Do tej pory jedyną drogą do linku był
                          schowek, a gdy ten odmówił (brak uprawnień w przeglądarce, zdalny pulpit),
                          obsługa zostawała bez adresu i bez sposobu, żeby do niego wrócić. Piotrek
                          stracił tak link do raty 2026-08-04. Tylko przy prośbach czekających na
                          klienta: przy opłaconej link jest już bez znaczenia, a przy wycofanej
                          pokazywanie go zachęcałoby do wysłania adresu, który nie zadziała. */}
                      {r.status === 'pending' && r.pay_url && (
                        <PayLinkBlock>
                          <PayLinkLabel>{t('payments.payLinkLabel')}</PayLinkLabel>
                          <PayLinkInput
                            readOnly
                            value={r.pay_url}
                            onClick={(e) => e.target.select()}
                            onFocus={(e) => e.target.select()}
                          />
                          {/* Adres jest dłuższy niż kolumna i zawsze będzie przycięty, więc mówimy
                              wprost, że kliknięcie zaznacza CAŁOŚĆ. Bez tego widok sugeruje, że
                              skopiuje się tylko widoczny fragment. */}
                          <PayLinkHint>{t('payments.payLinkHint')}</PayLinkHint>
                        </PayLinkBlock>
                      )}
                    </td>
                  </tr>
                );
              }); })()}
            </tbody>
          </Table>
        )}
      </TableWrap>

      <PaginationCompact
        page={safePage}
        totalPages={pageCount}
        onPageChange={setPage}
        pageSize={DEFAULT_PAGE_SIZE}
        totalItems={rows.length}
      />
    </PanelContainer>
  );
}

export default PaymentsList;
